import { api } from './api';

export interface InventoryCategory {
  _id: string;
  name: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Inventory {
  _id: string;
  name: string;
  description: string;
  category: string; // Category ID
  quantity: number;
  uom: string; // Unit of Measurement
  location: string; // Asset Location ID
  minThreshold?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateInventoryCategoryData {
  name: string;
  description: string;
}

export interface CreateInventoryData {
  name: string;
  description: string;
  category: string;
  quantity: number;
  uom: string;
  location: string;
  minThreshold?: number;
}

export interface UpdateInventoryData extends Partial<CreateInventoryData> {}

// API Response interfaces to match your backend structure
interface ApiResponse<T> {
  message: string;
  data: T;
  error: boolean;
}

interface PaginatedResponse<T> {
  result: T[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

class InventoryService {
  // Inventory Category endpoints
  async createInventoryCategory(data: CreateInventoryCategoryData): Promise<InventoryCategory> {
    const response = await api.post<ApiResponse<InventoryCategory>>('/inventory-category', data);
    return response.data.data;
  }

  async getInventoryCategories(): Promise<InventoryCategory[]> {
    const response = await api.get<ApiResponse<PaginatedResponse<InventoryCategory>>>('/inventory-category');
    return response.data.data.result;
  }

  async getInventoryCategory(id: string): Promise<InventoryCategory> {
    const response = await api.get<ApiResponse<InventoryCategory>>(`/inventory-category/${id}`);
    return response.data.data;
  }

  async updateInventoryCategory(id: string, data: Partial<CreateInventoryCategoryData>): Promise<InventoryCategory> {
    const response = await api.put<ApiResponse<InventoryCategory>>(`/inventory-category/${id}`, data);
    return response.data.data;
  }

  async deleteInventoryCategory(id: string): Promise<void> {
    await api.delete(`/inventory-category/${id}`);
  }

  // Inventory endpoints
  async createInventory(data: CreateInventoryData): Promise<Inventory> {
    const response = await api.post<ApiResponse<Inventory>>('/inventory', data);
    return response.data.data;
  }

  async getInventories(): Promise<Inventory[]> {
    const response = await api.get<ApiResponse<PaginatedResponse<Inventory>>>('/inventory');
    // Extract the result array from the paginated response
    return response.data.data.result;
  }

  async getInventory(id: string): Promise<Inventory> {
    const response = await api.get<ApiResponse<Inventory>>(`/inventory/${id}`);
    return response.data.data;
  }

  async updateInventory(id: string, data: UpdateInventoryData): Promise<Inventory> {
    const response = await api.put<ApiResponse<Inventory>>(`/inventory/${id}`, data);
    return response.data.data;
  }

  async deleteInventory(id: string): Promise<void> {
    await api.delete(`/inventory/${id}`);
  }
}

export const inventoryService = new InventoryService();